import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CustomerDocument } from '@app/models/customer-document';
import { PersonPortalState } from '@app/models/person-portal-state';
import { LocalStorageUtilities } from '@app/utilities/local-storage-utilities';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { LoggingService } from '@app/services/logging.service';
import { LogEventStrings } from '@app/utilities/log-event-strings';
import { MatSort } from '@angular/material/sort';
import { DatePipe } from '@angular/common';
import { LogEventCodes } from '@app/utilities/log-event-codes';


@Component({
  selector: 'app-edocuments',
  templateUrl: './edocuments.component.html',
  styleUrls: ['./edocuments.component.css']
})
export class EdocumentsComponent implements OnInit {

  public personPortalState: PersonPortalState;
  public showOverlay: boolean = false;
  public base64Src: string;
  public activeDocumentTypePdf: string;
  public activeDocumentDatePdf: Date;

  public statements: CustomerDocument[] = [];
  public confirmations: CustomerDocument[] = [];
  public summaries: CustomerDocument[] = [];

  public statementsDataSource = new MatTableDataSource<CustomerDocument>(this.statements);
  public statementsColumns: string[] = ['effectiveDate', 'description', 'pdf'];
  public confirmationsDataSource = new MatTableDataSource<CustomerDocument>(this.confirmations);
  public confirmationsColumns: string[] = ['effectiveDate', 'description', 'pdf'];
  public summariesDataSource = new MatTableDataSource<CustomerDocument>(this.summaries);
  public summariesColumns: string[] = ['effectiveDate', 'description', 'pdf'];

  @ViewChild("statementsPaginator", { static: true })
  public statementsPaginator: MatPaginator;
  @ViewChild("confirmationsPaginator", { static: true })
  public confirmationsPaginator: MatPaginator;
  @ViewChild("summariesPaginator", { static: true })
  public summariesPaginator: MatPaginator;
  @ViewChild("statementsSort", { static: true })
  public statementsSort: MatSort;
  @ViewChild("confirmationsSort", { static: true })
  public confirmationsSort: MatSort;
  @ViewChild("summariesSort", { static: true })
  public summariesSort: MatSort;

  constructor(
    private changeDetectorRefs: ChangeDetectorRef,
    private loggingService: LoggingService,
    public datepipe: DatePipe,
    ) { }

  ngOnInit() {
    this.statementsDataSource.sort = this.statementsSort;
    this.confirmationsDataSource.sort = this.confirmationsSort;
    this.summariesDataSource.sort = this.summariesSort;
    this.initControls();   
  }

  private initControls() {
    this.personPortalState = LocalStorageUtilities.getPersonPortalState();
    // Table paginator.
    this.statementsDataSource.paginator = this.statementsPaginator;
    this.confirmationsDataSource.paginator = this.confirmationsPaginator;
    this.summariesDataSource.paginator = this.summariesPaginator;
    // Force table to sort decendingly on tradeDate. 
    this.statementsSort.sort({ id: 'effectiveDate', start: 'desc', disableClear: false });
    this.confirmationsSort.sort({ id: 'effectiveDate', start: 'desc', disableClear: false });
    this.summariesSort.sort({ id: 'effectiveDate', start: 'desc', disableClear: false });
  }

  private loadDocuments(): void {
    this.personPortalState.customerDocuments.filter(document => {
      return document.type === "STATEMENT";
    }).forEach(statement => {
      this.statements.push(statement);
    });
    this.personPortalState.customerDocuments.filter(document => {
      return document.type === "CONFIRM";
    }).forEach(confirm => {
      this.confirmations.push(confirm);
    });
    this.personPortalState.customerDocuments.filter(document => {
      return document.type === "SUMMARY";
    }).forEach(summary => {
      this.summaries.push(summary);
    });
  }

  /**
   * Open PDF in the overlay's PDF viewer.
   * 
   * Note: Multiple methods were created so that logging 
   * could be done by type of documents.  Absent this requirement,
   * only one method would be needed.
   * @param row 
   */
  public onShowPdf(row: CustomerDocument): void {
    // Save document type information which will be used when the PDF
    // is printed.
    this.activeDocumentTypePdf = row.type;
    this.activeDocumentDatePdf = row.effectiveDate;
    switch (row.type) {
      case "STATEMENT":
        // Log the customer's event.        
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_VIEWED_STATEMENT, LogEventCodes.CUSTOMER_VIEWED_STATEMENT_CODE, "(" + this.activeDocumentTypePdf + ":" + this.formatTimestampToDate(this.activeDocumentDatePdf) + ")");
        break;
      case "CONFIRM":
        // Log the customer's event.
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_VIEWED_CONFIRMATION, LogEventCodes.CUSTOMER_VIEWED_CONFIRMATION_CODE, "(" + this.activeDocumentTypePdf + ":" + this.formatTimestampToDate(this.activeDocumentDatePdf) + ")");
        break;
      case "SUMMARY":
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_VIEWED_SUMMARY, LogEventCodes.CUSTOMER_VIEWED_SUMMARY_CODE, "(" + this.activeDocumentTypePdf + ":" + this.formatTimestampToDate(this.activeDocumentDatePdf) + ")");
    }
    this.base64Src = row.pdf;
    this.toggleOverlay();
  }

  public onPrintPdf(identifier: string, event: any) {
    switch (this.activeDocumentTypePdf) {
      case "STATEMENT":
        // Log the customer's event.        
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_PRINTED_STATEMENT, LogEventCodes.CUSTOMER_PRINTED_STATEMENT_CODE);
        break;
      case "CONFIRM":
        // Log the customer's event.
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_PRINTED_CONFIRMATION, LogEventCodes.CUSTOMER_PRINTED_CONFIRMATION_CODE);
        break;
      case "SUMMARY":
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_PRINTED_SUMMARY, LogEventCodes.CUSTOMER_PRINTED_SUMMARY_CODE);
    }
  }

  public onDownloadPdf(identifier: string, event: any) {
    switch (this.activeDocumentTypePdf) {
      case "STATEMENT":
        // Log the customer's event.        
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_DOWNLOADED_STATEMENT, LogEventCodes.CUSTOMER_DOWNLOADED_STATEMENT_CODE, "(" + this.activeDocumentTypePdf + ":" + this.formatTimestampToDate(this.activeDocumentDatePdf) + ")");
        break;
      case "CONFIRM":
        // Log the customer's event.
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_DOWNLOADED_CONFIRMATION, LogEventCodes.CUSTOMER_DOWNLOADED_CONFIRMATION_CODE, "(" + this.activeDocumentTypePdf + ":" + this.formatTimestampToDate(this.activeDocumentDatePdf) + ")");
        break;
      case "SUMMARY":
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_DOWNLOADED_SUMMARY, LogEventCodes.CUSTOMER_DOWNLOADED_SUMMARY_CODE, "(" + this.activeDocumentTypePdf + ":" + this.formatTimestampToDate(this.activeDocumentDatePdf) + ")");
    }
  }

  public toggleOverlay(): void {
    this.showOverlay = !this.showOverlay;
  }

  /**
   * Called from the "Main" module when the selected account changes.
   */
  public refreshUI(): void {
    // Get latest account information.
    this.personPortalState = LocalStorageUtilities.getPersonPortalState();
    // Clear the models.
    this.statements = [];
    this.confirmations = [];
    this.summaries = [];
    // Load the models.
    this.loadDocuments();
    this.statementsDataSource.data = this.statements;
    this.confirmationsDataSource.data = this.confirmations;
    this.summariesDataSource.data = this.summaries;
    this.changeDetectorRefs.detectChanges();
  }

  

  private formatTimestampToDate(date: Date) {
    return this.datepipe.transform(date, 'yyyy-MM-dd');
  }

}
